import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { ThemeContext, type Theme } from './theme-context';

const STORAGE_KEY = 'dogitec.theme';

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Ventana privada o almacenamiento bloqueado por politica: se continua con
    // la preferencia del sistema en lugar de romper el arranque.
  }
  return 'system';
}

/**
 * Tema de la interfaz.
 *
 * Por defecto sigue al sistema operativo: no se impone oscuro ni claro. Un
 * almacen con poca luz y una oficina con ventanales tienen necesidades
 * opuestas, y el sistema operativo ya conoce la respuesta.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    const root = document.documentElement;

    if (theme === 'system') {
      // Sin atributo: el CSS cae en la consulta prefers-color-scheme.
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // La preferencia se pierde al recargar, pero la sesion actual funciona.
    }
  }, []);

  return <ThemeContext value={{ theme, setTheme }}>{children}</ThemeContext>;
}
